// Production-ready API base URL with fallback strategies
const getApiBaseUrl = (): string => {
  if (typeof window === 'undefined') {
    return '/api/v1'; // SSR fallback
  }
  
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // Production: Always try relative proxy first for CloudFlare Pages
  if (hostname.includes('qutec.it')) {
    return '/api/v1';
  }
  
  // Development/localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return import.meta.env.VITE_API_BASE_URL || '/api/v1';
  }
  
  // Default: relative proxy path
  return '/api/v1';
};

// Enhanced fetch function with automatic fallback for production
const createRobustFetch = () => {
  const primaryBaseUrl = getApiBaseUrl();
  const fallbackBaseUrl = 'https://documenti-api.qutec.it/api/v1';
  
  return async (endpoint: string, options: RequestInit = {}) => {
    // First attempt: Use primary URL (proxy)
    try {
      const response = await fetch(`${primaryBaseUrl}${endpoint}`, {
        ...options,
        credentials: 'include'
      });
      
      // If we get a response (even error), return it
      if (response.status !== 502 && response.status !== 503 && response.status !== 504) {
        return response;
      }
      
      console.warn(`Primary API failed with ${response.status}, trying fallback...`);
    } catch (error) {
      console.warn('Primary API request failed, trying fallback...', error);
    }
    
    // Fallback attempt: Direct API call with CORS (only for production)
    if (window.location.hostname.includes('qutec.it')) {
      try {
        console.info('Attempting fallback to direct API endpoint...');
        const fallbackOptions = {
          ...options,
          mode: 'cors' as RequestMode,
          headers: {
            ...options.headers,
            'Access-Control-Allow-Credentials': 'true'
          }
        };
        
        // Keep credentials for same-site requests
        if (window.location.hostname.includes('qutec.it')) {
          fallbackOptions.credentials = 'include';
        }
        
        const response = await fetch(`${fallbackBaseUrl}${endpoint}`, fallbackOptions);
        
        if (response.ok || response.status < 500) {
          console.info('Fallback API successful');
          return response;
        }
        
        throw new Error(`Fallback API returned ${response.status}`);
      } catch (error) {
        console.error('Fallback API also failed:', error);
        throw new Error('Service temporarily unavailable. Please try again in a few moments.');
      }
    }
    
    throw new Error('API endpoint unavailable');
  };
};

const robustFetch = createRobustFetch();
const API_BASE_URL = getApiBaseUrl();

// ============================================================================
// REQUEST DEDUPLICATION - Prevents duplicate simultaneous API calls
// ============================================================================
// Stores pending requests to prevent duplicate calls for the same resource
const pendingRequests = new Map<string, Promise<Response>>();

/**
 * Deduplicates API requests - if the same request is already in flight,
 * returns the existing promise instead of making a new request.
 * This prevents duplicate API traffic when components mount simultaneously.
 */
const deduplicatedFetch = async (
  cacheKey: string,
  fetcher: () => Promise<Response>
): Promise<Response> => {
  // Check if request is already in flight
  if (pendingRequests.has(cacheKey)) {
    console.debug(`[Dedup] Reusing in-flight request: ${cacheKey}`);
    return pendingRequests.get(cacheKey)!;
  }

  // Create new request and store it
  const request = fetcher().finally(() => {
    // Remove from pending after completion (success or failure)
    pendingRequests.delete(cacheKey);
  });

  pendingRequests.set(cacheKey, request);
  return request;
};

/**
 * Simple in-memory cache with TTL for API responses
 * Used for frequently accessed, slow-changing data
 */
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

const responseCache = new Map<string, CacheEntry<unknown>>();

const getCachedResponse = <T>(key: string): T | null => {
  const entry = responseCache.get(key) as CacheEntry<T> | undefined;
  if (!entry) return null;

  const now = Date.now();
  if (now - entry.timestamp > entry.ttl) {
    responseCache.delete(key);
    return null;
  }

  return entry.data;
};

const setCachedResponse = <T>(key: string, data: T, ttlMs: number = 60000): void => {
  responseCache.set(key, {
    data,
    timestamp: Date.now(),
    ttl: ttlMs
  });
};

// ============================================================================

// Utility function to get authentication headers
export const getAuthHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  // Session-based authentication - no need for custom headers
  
  // Add user email header if available
  try {
    const userEmail = localStorage.getItem('kms_user_email');
    if (userEmail) {
      // Remove quotes if present
      let email = userEmail;
      if (email.startsWith('"') && email.endsWith('"')) {
        email = email.substring(1, email.length - 1);
      }
      headers['X-KMS-User-Email'] = email;
    }
  } catch (e) {
    console.error('Error adding user email to headers:', e);
  }
  
  return headers;
};

// Enhanced fetch function with auth error handling
const fetchWithAuth = async (url: string, options: RequestInit): Promise<Response> => {
  // FIRST check if we're in an auth flow - this is the most important check
  const authFlowInProgress = sessionStorage.getItem('auth_flow_in_progress') === 'true';
  
  // Disable auth flow debug logging to reduce console noise
  // Enable only when debugging auth flow issues: if (authFlowInProgress) {
  if (false && authFlowInProgress) {
    console.warn(`[Auth Flow Debug] Request to ${url} during active auth flow`);
  }
  
  try {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout for document lists
    
    // Add abort signal to options
    const optionsWithTimeout = {
      ...options,
      signal: controller.signal
    };
    
    const response = await fetch(url, optionsWithTimeout);
    clearTimeout(timeoutId);
    
    // Handle authentication errors (401/403)
    if (response.status === 401 || response.status === 403) {
      console.warn('Authentication error detected:', response.status, response.statusText);
      
      // If we're in an auth flow, don't trigger any redirects or logout
      if (authFlowInProgress) {
        console.warn('In authentication flow - skipping auth redirect to prevent loops');
        return response;
      }
      
      // Check if this is a configuration request - if so, don't trigger redirects
      if (url.includes('/user-config/')) {
        console.warn('Auth error on configuration endpoint - not redirecting to avoid loops');
        return response;
      }
      
      // Try to parse the response to get more details
      try {
        const errorData = await response.clone().json();
        console.warn('Auth error details:', errorData);
        
        // Check if this is a login-required error
        if (errorData?.detail?.type === 'authentication_required' || 
            errorData?.message?.includes('authentication') ||
            errorData?.message?.includes('login')) {
          
          // Check if we're already on the login page or in an auth flow
          const isLoginPage = window.location.pathname === '/login';
          const isAuthCallback = window.location.pathname.includes('/auth/callback');
          
          // Prevent redirect loops - only redirect if not already on login or auth pages
          if (!isLoginPage && !isAuthCallback) {
            // Check if we've redirected recently
            const lastRedirectTime = sessionStorage.getItem('last_auth_redirect_time');
            const currentTime = Date.now();
            
            if (!lastRedirectTime || (currentTime - parseInt(lastRedirectTime)) > 10000) { // Increased from 5000 to 10000ms
              // Store redirect time to prevent loops
              sessionStorage.setItem('last_auth_redirect_time', currentTime.toString());
              
              // Clear any existing auth data
              localStorage.removeItem('kms_auth_user');
              localStorage.removeItem('kms_auth_check');
              
              // Redirect to login after a short delay
              console.log('Redirecting to login page due to auth error');
              setTimeout(() => {
                window.location.href = '/login';
              }, 100);
            } else {
              console.warn('Prevented redirect loop - redirected too recently');
            }
          } else {
            console.warn('Not redirecting to login - already on login or auth callback page');
          }
        }
      } catch (parseError) {
        console.error('Error parsing auth error response:', parseError);
        
        // Skip auth redirect if we're in an auth flow
        if (sessionStorage.getItem('auth_flow_in_progress')) {
          console.warn('In authentication flow - skipping auth redirect to prevent loops');
          return response;
        }
        
        // Simpler check to avoid redirect loops - only redirect if not already on login
        if (window.location.pathname !== '/login' && 
            !window.location.pathname.includes('/auth/callback')) {
          
          // Check for recent redirects
          const lastRedirectTime = sessionStorage.getItem('last_auth_redirect_time');
          const currentTime = Date.now();
          
          if (!lastRedirectTime || (currentTime - parseInt(lastRedirectTime)) > 10000) { // Increased from 5000 to 10000ms
            sessionStorage.setItem('last_auth_redirect_time', currentTime.toString());
            setTimeout(() => {
              window.location.href = '/login';
            }, 100);
          } else {
            console.warn('Prevented redirect loop - redirected too recently');
          }
        }
      }
    }
    
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('Request timeout after 5 minutes:', url);
      throw new Error('La richiesta ha richiesto troppo tempo. Prova a ridurre il numero di documenti o riprova più tardi.');
    }
    console.error('Network error in fetchWithAuth:', error);
    throw error;
  }
};

// Utility function to construct fetch options with authentication
const getFetchOptions = (method: string = 'GET', body?: any): RequestInit => {
  const options: RequestInit = {
    method,
    headers: {
      ...getAuthHeaders(),
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache'
    },
    credentials: 'include', // Always include credentials for cookies
  };
  
  if (body) {
    options.body = JSON.stringify(body);
  }
  
  return options;
};

// Helper to check if a user is authenticated
export const checkAuthenticated = async (): Promise<boolean> => {
  try {
    console.debug('Checking authentication status...');
    const headers = getAuthHeaders();
    console.debug('Auth headers:', headers);
    
    const response = await fetch(`${API_BASE_URL}/auth/status`, {
      method: 'GET',
      headers: {
        ...headers,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      console.error('Auth check failed:', response.status, response.statusText);
      return false;
    }
    
    const data = await response.json();
    console.debug('Auth check result:', data);
    return data.success === true;
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
};

export interface DocumentVersion {
  version_id: string;
  last_modified: string;
  is_latest: boolean;
  size: number;
}

export interface DocumentMetadata {
  title?: string;
  author?: string;
  created_date?: string;
  modified_date?: string;
  content_type?: string;
  file_type?: string;
  size?: number;
  page_count?: number;
}

export interface UploadResponse {
  file_name: string;
  version_id: string | null;
  success: boolean;
  message: string;
  metadata?: DocumentMetadata;
}

export interface VersionListResponse {
  file_name: string;
  versions: DocumentVersion[];
  count: number;
}

export interface PresignedUrlResponse {
  url: string;
  expiration: number;
  file_name: string;
}

export interface DocumentInfo {
  file_name: string;
  file_id: string;
  version_id?: string;
  last_modified: string;
  size: number;
  is_latest: boolean;
  content_type?: string;
  md5_checksum?: string;
  file_path?: string;
  source_folder_name?: string;
  source_folder_id?: string;
  source_collection?: string;
}

export interface DocumentListResponse {
  documents: DocumentInfo[];
  count: number;
}

export interface DocumentContentResponse {
  content: string;
}

export interface SearchResult {
  content: string;
  metadata: {
    file_name: string;
    file_id: string;
    title?: string;
    content_type?: string;
    source?: string;
    file_path?: string;
    [key: string]: any;
  };
  score: number;
}

export interface SearchResponse {
  results: SearchResult[];
  count: number;
  success: boolean;
  query: string;
  message?: string;
}

export interface EmbeddingResponse {
  success: boolean;
  message: string;
  collection_name?: string;
  chunk_count?: number;
  file_id?: string;
  file_name?: string;
  already_embedded?: boolean;
  unchanged?: boolean;
  version?: number;
}

export interface EmbeddingResult {
  file_name: string;
  file_id: string;
  success: boolean;
  message: string;
  chunk_count: number;
  already_embedded?: boolean;
  unchanged?: boolean;
  version?: number;
}

export interface EmbedAllResponse {
  success: boolean;
  message: string;
  results?: EmbeddingResult[];
  successful_count?: number;
  updated_count?: number;
  skipped_count?: number;
  failed_count?: number;
  total_count?: number;
}

export interface DocumentChangeCheckResult {
  file_id: string;
  file_name: string;
  changed: boolean;
  update_success?: boolean;
  error?: boolean;
  message: string;
}

export interface DocumentChangeResponse {
  success: boolean;
  message: string;
  documents_checked?: number;
  changes_detected?: number;
  update_success?: number;
  update_failed?: number;
  results?: DocumentChangeCheckResult[];
}

export interface EmbeddingStatsResponse {
  success: boolean;
  message: string;
  total_documents: number;
  embedded_documents: number;
  pending_documents: number;
  embedding_percentage: number;
  orphaned_embeddings: number;
  embedding_status?: Array<{
    file_id: string;
    file_name: string;
    file_path: string;
    is_embedded: boolean;
    last_embedded?: string;
    embedding_status?: string;
    chunk_count?: number;
    version?: number;
  }>;
}

export interface EmbedNewDocumentsResponse {
  success: boolean;
  message: string;
  results?: EmbeddingResult[];
  successful_count?: number;
  updated_count?: number;
  embedded_count?: number;
  skipped_count?: number;
  failed_count?: number;
  total_count?: number;
  total_time?: number;
  failed_documents?: Array<{
    file_id: string;
    file_name: string;
    error: string;
  }>;
  skipped_documents?: Array<{
    file_id: string;
    file_name: string;
    reason: string;
  }>;
}

export interface FindNewDocumentsResponse {
  success: boolean;
  message: string;
  new_documents: Array<{
    file_id: string;
    file_name: string;
    [key: string]: any;
  }>;
  total_drive_files?: number;
  total_embedded_files?: number;
}

export interface UserConfigurationRequest {
  folder_id: string;
  display_name: string;
  organization_name: string;
  is_default?: boolean;
}

export interface UserConfigurationResponse {
  config_id: string;
  folder_id: string;
  collection_name: string;
  display_name: string;
  organization_name: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  access_validated: boolean;
  user_email?: string;
  organization_id?: string;
}

export interface DatabaseFolderValidationResult {
  folder_accessible: boolean;
  folder_name?: string;
  folder_path?: string;
  error_message?: string;
}

export interface FolderInfo {
  folder_id: string;
  folder_name: string;
  folder_path: string;
  parent_id?: string;
}

export interface QARequest {
  query: string;
}

export interface QAResponse {
  answer: string;
  sources: Array<{
    content: string;
    metadata: Record<string, any>;
    score: number;
  }>;
  success: boolean;
}



export const DocumentService = {
  // Helper function to safely construct URLs with query parameters
  buildApiUrl(path: string, params?: Record<string, string>): string {
    // If API_BASE_URL is relative, construct URL without using new URL()
    let url = API_BASE_URL.startsWith('/') 
      ? `${window.location.origin}${API_BASE_URL}${path}`
      : `${API_BASE_URL}${path}`;
    
    // Add query parameters if provided
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }
    
    return url;
  },
  async listDocuments(forceRefresh: boolean = false, page: number = 1, pageSize: number = 100): Promise<DocumentListResponse> {
    try {
      // First, check if we have a valid configuration before attempting to list documents
      try {
        const configurations = await UserConfigService.getConfigurations();
        const validConfigs = configurations.filter(config =>
          config && config.folder_id && config.folder_id.trim() !== ""
        );

        if (!validConfigs.length) {
          console.warn("No valid configurations with folder IDs found before attempting to list documents");
          throw {
            status: 404,
            message: "No active configuration with valid folder ID",
            isConfigurationError: true,
            response: {
              status: 404,
              data: { detail: "No active configuration with valid folder ID" }
            }
          };
        }
      } catch (configError: any) {
        if (configError.isConfigurationError) {
          throw configError;
        }
        // If it's some other error, continue and let the document fetch try anyway
        console.warn("Error checking configurations, will try document fetch anyway:", configError);
      }

      const options = getFetchOptions();

      // TWO-PHASE LOADING STRATEGY:
      // Phase 1: When NOT forcing refresh, try database cache first for instant response (~2s)
      // Phase 2: If forceRefresh=true, go through Google Drive API (slower, up to 90s)
      //
      // This dramatically improves UX - users see documents in <3 seconds instead of 75+ seconds
      let endpoint: string;
      if (forceRefresh) {
        // Force refresh: Use Google Drive API (slow but gets latest data)
        console.log('[listDocuments] Force refresh requested - using Google Drive API');
        endpoint = `${API_BASE_URL}/documents/list-all?force_refresh=true&use_cache=false&page=${page}&page_size=${pageSize}`;
      } else {
        // Normal load: Use fast database cache endpoint
        console.log('[listDocuments] Using fast database cache endpoint for instant loading');
        endpoint = `${API_BASE_URL}/documents/list-cached?page=${page}&page_size=${pageSize}`;
      }

      // Use request deduplication to prevent duplicate API calls
      // This is especially important when multiple components mount simultaneously
      const cacheKey = `listDocs:${page}:${pageSize}:${forceRefresh}`;
      const startTime = Date.now();
      const response = await deduplicatedFetch(cacheKey, () => fetchWithAuth(endpoint, options));
      const elapsed = Date.now() - startTime;
      console.log(`[listDocuments] API response in ${elapsed}ms from ${forceRefresh ? 'Google Drive' : 'database cache'}`);

      if (!response.ok) {
        // Special handling for 404 errors - treat as configuration issue
        if (response.status === 404) {
          console.warn("Document endpoint returned 404, treating as configuration issue");
          throw {
            status: 404,
            message: "Document endpoint not found - likely configuration issue",
            isConfigurationError: true,
            response: {
              status: 404,
              data: { detail: "No active configuration with valid folder ID" }
            }
          };
        }

        const errorText = await response.text();
        let detail;
        try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorText;
        } catch (e) { /* Ignore parsing error */ }
        throw {
          status: response.status,
          message: detail || response.statusText,
          response: { status: response.status, data: { detail } }
        };
      }

      return response.json();
    } catch (error) {
      console.error('Error listing documents:', error);
      throw error;
    }
  },

  async uploadDocument(file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/documents/upload`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${errorText}`);
    }

    return response.json();
  },

  async extractMetadata(file: File): Promise<DocumentMetadata> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/documents/extract-metadata`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Metadata extraction failed: ${errorText}`);
    }

    return response.json();
  },

  async getDocumentVersions(fileName: string): Promise<VersionListResponse> {
    const response = await fetch(`${API_BASE_URL}/documents/versions/${fileName}`, {
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get versions: ${errorText}`);
    }

    return response.json();
  },

  async deleteDocument(fileName: string, versionId?: string): Promise<void> {
    const url = versionId 
      ? `${API_BASE_URL}/documents/${fileName}?version_id=${versionId}`
      : `${API_BASE_URL}/documents/${fileName}`;

    const response = await fetch(url, {
      method: 'DELETE',
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Delete failed: ${errorText}`);
    }
  },

  async getPresignedDownloadUrl(fileName: string, versionId?: string): Promise<PresignedUrlResponse> {
    const params: Record<string, string> = { file_name: fileName, operation: 'get_object' };
    if (versionId) {
      params.version_id = versionId;
    }
    const url = DocumentService.buildApiUrl('/documents/presigned-url', params);

    const response = await fetch(url, { credentials: 'include' });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get presigned URL: ${errorText}`);
    }

    return response.json();
  },

  async getPresignedUploadUrl(fileName: string): Promise<PresignedUrlResponse> {
    const params = { file_name: fileName, operation: 'put_object' };
    const url = DocumentService.buildApiUrl('/documents/presigned-url', params);

    const response = await fetch(url, { credentials: 'include' });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get presigned URL: ${errorText}`);
    }

    return response.json();
  },

  // Get a pre-signed URL for GET operations
  async getPresignedGetUrl(fileName: string, versionId?: string): Promise<PresignedUrlResponse> {
    const params: Record<string, string> = { file_name: fileName, operation: 'get_object' };
    if (versionId) {
      params.version_id = versionId;
    }
    const url = DocumentService.buildApiUrl('/documents/presigned-url', params);

    const response = await fetch(url, { credentials: 'include' });

    if (!response.ok) {
      const errorText = await response.text();
      // Try to parse backend error detail
      let detail = errorText;
      try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorText;
      } catch (e) { /* Ignore parsing error */ }
      throw new Error(`Failed to get presigned URL: ${detail}`);
    }

    return response.json();
  },

  getDownloadUrl(fileName: string, versionId?: string): string {
    const params = versionId ? { version_id: versionId } : undefined;
    return DocumentService.buildApiUrl(`/documents/download/${fileName}`, params);
  },

  async getDocumentMetadata(fileNameOrId: string, versionId?: string): Promise<DocumentMetadata> {
    const params = versionId ? { version_id: versionId } : undefined;
    const url = DocumentService.buildApiUrl(`/documents/metadata/${fileNameOrId}`, params);

    const response = await fetch(url, { credentials: 'include' });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get metadata: ${errorText}`);
    }

    return response.json();
  },

  getViewUrl(fileName: string, versionId?: string): string {
    const params: Record<string, string> = {};
    
    if (versionId) {
      params.version_id = versionId;
    }

    return this.buildApiUrl(`/documents/view/${fileName}`, params);
  },

  async getDocumentContent(fileId: string, versionId?: string): Promise<DocumentContentResponse> {
    const encodedFileId = encodeURIComponent(fileId);
    const queryParams = versionId ? `?version_id=${versionId}` : '';
    const url = `${API_BASE_URL}/documents/${encodedFileId}/content${queryParams}`;

    const response = await fetch(url, { credentials: 'include' });

    if (!response.ok) {
      const errorText = await response.text();
      let detail = errorText;
      try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorText;
      } catch (e) { /* Ignore parsing error */ }
      throw new Error(`Failed to get document content: ${detail}`);
    }

    return response.json();
  },

  // Search embedded documents using vector search
  async searchDocuments(query: string, topK: number = 5): Promise<SearchResponse> {
    try {
      // Use simple format that matches current codebase
      const requestBody = {
        query: query,
        top_k: topK
      };
      
      const response = await robustFetch('/vectorstore/search', {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
          
          // Enhanced error handling to properly convert objects to strings
          if (typeof detail !== 'string') {
            if (Array.isArray(detail)) {
              // Handle array of errors
              detail = detail.map(item => {
                if (typeof item === 'string') {
                  return item;
                } else if (typeof item === 'object' && item !== null) {
                  // Convert object to meaningful string
                  if (item.message) return item.message;
                  if (item.error) return item.error;
                  if (item.detail) return item.detail;
                  if (item.msg) return item.msg; // FastAPI validation error
                  return Object.prototype.toString.call(item) === '[object Object]' 
                    ? JSON.stringify(item) 
                    : String(item);
                }
                return String(item);
              }).join(', ');
            } else if (typeof detail === 'object' && detail !== null) {
              // Handle single object error
              if (detail.message) {
                detail = detail.message;
              } else if (detail.error) {
                detail = detail.error;
              } else if (detail.detail) {
                detail = detail.detail;
              } else if (detail.msg) {
                detail = detail.msg; // FastAPI validation error
              } else {
                detail = JSON.stringify(detail);
              }
            } else {
              detail = String(detail);
            }
          } else {
            // Handle case where detail is already a string but might be a JSON string
            try {
              const parsedDetail = JSON.parse(detail);
              if (Array.isArray(parsedDetail)) {
                // Handle JSON string that contains an array of error objects
                detail = parsedDetail.map(item => {
                  if (typeof item === 'object' && item !== null) {
                    return item.msg || item.message || item.error || item.detail || JSON.stringify(item);
                  }
                  return String(item);
                }).join(', ');
              }
            } catch {
              // If it's not valid JSON, keep the original string
            }
          }
        } catch (e) { 
          detail = errorText || 'Unknown error';
        }
        throw new Error(`Search failed: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error searching documents:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async checkDocumentChanges(): Promise<DocumentChangeResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/vectorstore/check-document-changes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to check document changes: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error checking document changes:', error);
      throw error;
    }
  },

  async getEmbeddingStats(): Promise<EmbeddingStatsResponse> {
    try {
      const response = await robustFetch('/vectorstore/embedding-stats', {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          // Handle structured error responses
          if (errorJson.detail && typeof errorJson.detail === 'object') {
            detail = errorJson.detail.message || errorJson.detail.type || errorText;
          } else {
            detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
          }
        } catch (e) { /* Ignore parsing error */ }
        
        // Provide more specific error messages based on status code
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in again.');
        } else if (response.status === 502) {
          throw new Error('Unable to connect to Google Drive service. Please try again later.');
        } else {
          throw new Error(`Failed to get embedding stats: ${detail}`);
        }
      }

      // Try to parse the response
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse embedding stats response:', responseText);
        throw new Error(`Invalid JSON response from server: ${responseText.substring(0, 200)}`);
      }
      
      // Validate the response structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format from server');
      }
      
      // Ensure required fields are present with defaults
      const validatedData: EmbeddingStatsResponse = {
        success: data.success ?? false,
        message: data.message || '',
        total_documents: data.total_documents || 0,
        embedded_documents: data.embedded_documents || 0,
        pending_documents: data.pending_documents || 0,
        embedding_percentage: data.embedding_percentage || 0,
        orphaned_embeddings: data.orphaned_embeddings || 0,
        embedding_status: data.embedding_status || []
      };
      
      return validatedData;

    } catch (error) {
      console.error('Error fetching embedding stats:', error);
      // Ensure we always throw a proper Error with a string message
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async embedNewDocuments(): Promise<EmbedNewDocumentsResponse> {
    try {
      const response = await robustFetch('/vectorstore/embed-new', {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to embed new documents: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error embedding new documents:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async embedSingleDocument(fileId: string, forceReembed: boolean = false): Promise<EmbeddingResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/vectorstore/embed`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_id: fileId,
          force_reembed: forceReembed
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to embed document: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error embedding document:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async checkDocumentChangesAsync(maxConcurrentTasks: number = 5): Promise<DocumentChangeResponse> {
    try {
      const params: Record<string, string> = {};
      if (maxConcurrentTasks !== 5) {
        params.max_concurrent_tasks = maxConcurrentTasks.toString();
      }
      
      const url = this.buildApiUrl('/vectorstore/check-document-changes-async', params);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to check document changes asynchronously: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error checking document changes asynchronously:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async embedNewDocumentsAsync(maxConcurrentTasks: number = 5): Promise<EmbedNewDocumentsResponse> {
    try {
      const params = maxConcurrentTasks !== 20 ? { max_concurrent_tasks: maxConcurrentTasks.toString() } : undefined;
      const url = DocumentService.buildApiUrl('/vectorstore/embed-new-async', params);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to embed new documents asynchronously: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error embedding new documents asynchronously:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async cleanupStaleEmbeddings(): Promise<{ success: boolean; deleted_count?: number; message?: string }> {
    try {
      const response = await robustFetch('/vectorstore/cleanup-stale', {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
        } catch (e) { /* Ignore parsing error */ }
        
        // Return error response for handling
        return {
          success: false,
          message: detail
        };
      }

      return response.json();
    } catch (error) {
      console.error('Error cleaning up stale embeddings:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error during cleanup'
      };
    }
  },

  async embedRemainingDocuments(): Promise<EmbedNewDocumentsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/vectorstore/embed-remaining`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to embed remaining documents individually: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error embedding remaining documents individually:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async embedAllDocumentsAsync(forceReembed: boolean = false, maxConcurrentTasks: number = 5): Promise<EmbedAllResponse> {
    try {
      const params: Record<string, string> = {};
      if (forceReembed) {
        params.force_reembed = 'true';
      }
      if (maxConcurrentTasks !== 5) {
        params.max_concurrent_tasks = maxConcurrentTasks.toString();
      }
      const url = DocumentService.buildApiUrl('/vectorstore/embed-all-async', Object.keys(params).length > 0 ? params : undefined);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to embed all documents asynchronously: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error embedding all documents asynchronously:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async findNewDocuments(): Promise<FindNewDocumentsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/vectorstore/find-new`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to find new documents: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error finding new documents:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async embedAllDocuments(forceReembed: boolean = false): Promise<EmbedAllResponse> {
    try {
      const params = forceReembed ? { force_reembed: 'true' } : undefined;
      const url = DocumentService.buildApiUrl('/vectorstore/embed-all', params);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to embed all documents: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error embedding all documents:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  }
};

export const UserConfigService = {
  // Cache for configuration data
  _configCache: null as UserConfigurationResponse[] | null,
  _configCacheTime: 0,
  _configCacheMaxAge: 60000, // 1 minute cache

  async getConfigurations(): Promise<UserConfigurationResponse[]> {
    // Check cache first
    const now = Date.now();
    const cacheAge = now - this._configCacheTime;
    
    if (this._configCache && cacheAge < this._configCacheMaxAge) {
      // Disable cache logging to reduce console noise
      // Enable only when debugging: console.log(`Using cached configuration data (${Math.round(cacheAge / 1000)}s old)`);
      return this._configCache;
    }

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/configurations`, getFetchOptions());
      
      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 404) {
          // Only log in debug mode
          if (import.meta.env.DEV && false) { // Disabled to reduce console noise
            console.log('No configurations found for user');
          }
          const emptyResult: UserConfigurationResponse[] = [];
          // Cache empty result for a shorter time
          this._configCache = emptyResult;
          this._configCacheTime = now - (this._configCacheMaxAge - 10000); // Cache for only 10 seconds if empty
          return emptyResult;
        }
        
        const errorText = await response.text();
        console.error('Error response getting configurations:', response.status, errorText);
        throw new Error(`Failed to get configurations: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // Disable fresh data logging to reduce console noise
      // Enable only when debugging: console.log('Fresh configuration data loaded from API');
      
      // Update cache
      this._configCache = data;
      this._configCacheTime = now;
      
      return data;
    } catch (error) {
      console.error('Error getting configurations:', error);
      // Don't cache errors, but return cached data if available
      if (this._configCache) {
        // Disable stale data logging to reduce console noise
        // Enable only when debugging: console.log('Returning stale cached data due to error');
        return this._configCache;
      }
      throw error;
    }
  },

  // Clear cache when configurations change
  _clearConfigCache() {
    this._configCache = null;
    this._configCacheTime = 0;
  },

  async getDefaultConfiguration(): Promise<UserConfigurationResponse | null> {
    try {
      const options = getFetchOptions();
      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/configurations/default`, options);

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorText = await response.text();
        throw new Error(`Failed to get default configuration: ${errorText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error getting default configuration:', error);
      throw error;
    }
  },

  async createConfiguration(request: UserConfigurationRequest): Promise<UserConfigurationResponse> {
    try {
      // Validate the request
      if (!request.folder_id || !request.display_name) {
        throw new Error('Missing required fields: folder_id and display_name are required');
      }

      console.log('Creating configuration:', request);

      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/configurations`, getFetchOptions('POST', request));

      if (!response.ok) {
        let errorMessage = `Failed to create configuration: ${response.status} ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (errorData.detail.message) {
              errorMessage = errorData.detail.message;
            }
          }
        } catch (e) {
          // If we can't parse the error response, use the original message
          console.error('Failed to parse error response:', e);
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Configuration created successfully:', data);
      
      // Clear configuration cache since we've added a new config
      this._clearConfigCache();
      
      return data;
    } catch (error) {
      console.error('Error creating configuration:', error);
      throw error;
    }
  },

  async updateConfiguration(configId: string, request: UserConfigurationRequest): Promise<UserConfigurationResponse> {
    try {
      console.log('Updating configuration:', configId, request);

      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/configurations/${configId}`, getFetchOptions('PUT', request));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response updating configuration:', response.status, errorText);
        throw new Error(`Failed to update configuration: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Configuration updated successfully:', data);
      
      // Clear configuration cache since we've modified a config
      this._clearConfigCache();
      
      return data;
    } catch (error) {
      console.error('Error updating configuration:', error);
      throw error;
    }
  },

  async deleteConfiguration(configId: string): Promise<void> {
    try {
      console.log('Deleting configuration:', configId);

      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/configurations/${configId}`, getFetchOptions('DELETE'));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response deleting configuration:', response.status, errorText);
        throw new Error(`Failed to delete configuration: ${response.status} ${response.statusText}`);
      }

      console.log('Configuration deleted successfully');
      
      // Clear configuration cache since we've deleted a config
      this._clearConfigCache();
    } catch (error) {
      console.error('Error deleting configuration:', error);
      throw error;
    }
  },

  async validateFolder(request: { folder_id: string }): Promise<DatabaseFolderValidationResult> {
    try {
      console.log('Validating folder...', request.folder_id);
      
      const options = getFetchOptions('POST', request);
      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/validate-folder`, options);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to validate folder: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`Failed to validate folder: ${errorText}`);
      }

      const data = await response.json();
      console.log('Folder validation result:', data);
      return data;
    } catch (error) {
      console.error('Error validating folder:', error);
      throw error;
    }
  },

  async getAccessibleFolders(): Promise<FolderInfo[]> {
    try {
      const options = getFetchOptions();
      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/my-folders`, options);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response getting accessible folders:', response.status, errorText);
        throw new Error(`Failed to get accessible folders: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Accessible folders loaded:', data);
      return data;
    } catch (error) {
      console.error('Error getting accessible folders:', error);
      throw error;
    }
  },

  async getMyFolders(): Promise<FolderInfo[]> {
    // Alias for getAccessibleFolders for backward compatibility
    return this.getAccessibleFolders();
  }
};

// Uploaded document interfaces
export interface UploadedDocumentInfo {
  file_id: string;
  organization_id: string;
  filename: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  md5_checksum: string;
  s3_key: string;
  s3_bucket: string;
  source: string;
  uploaded_at: string;
  uploaded_by_email?: string;
  is_embedded: boolean;
  embedding_status?: string;
}

export interface ManualUploadResponse {
  file_id: string;
  filename: string;
  size_bytes: number;
  content_type: string;
  message: string;
  success: boolean;
}

export interface UploadedDocumentListResponse {
  documents: UploadedDocumentInfo[];
  count: number;
}

export const UploadedDocumentService = {
  async uploadDocument(file: File, organizationId: string): Promise<ManualUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('organization_id', organizationId);

    const response = await fetch(`${API_BASE_URL}/documents/upload`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Upload failed: ${response.status}`);
    }

    return response.json();
  },

  async listUploadedDocuments(organizationId: string): Promise<UploadedDocumentListResponse> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/documents/uploaded?organization_id=${encodeURIComponent(organizationId)}`,
      getFetchOptions()
    );

    if (!response.ok) {
      const errorText = await response.text();
      let detail = errorText;
      try {
        const errorJson = JSON.parse(errorText);
        detail = errorJson.detail || errorText;
      } catch (e) { /* Ignore */ }
      throw new Error(`Failed to list uploaded documents: ${detail}`);
    }

    return response.json();
  },

  async deleteUploadedDocument(fileId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/documents/uploaded/${encodeURIComponent(fileId)}`,
      getFetchOptions('DELETE')
    );

    if (!response.ok) {
      const errorText = await response.text();
      let detail = errorText;
      try {
        const errorJson = JSON.parse(errorText);
        detail = errorJson.detail || errorText;
      } catch (e) { /* Ignore */ }
      throw new Error(`Failed to delete document: ${detail}`);
    }

    return response.json();
  },

  getDownloadUrl(fileId: string): string {
    return `${API_BASE_URL}/documents/uploaded/${encodeURIComponent(fileId)}/download`;
  },

  getContentUrl(fileId: string): string {
    return `${API_BASE_URL}/documents/uploaded/${encodeURIComponent(fileId)}/content`;
  },

  async embedUploadedDocument(fileId: string): Promise<EmbeddingResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/vectorstore/embed-uploaded`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ file_id: fileId }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = (typeof errorJson.detail === 'object' && errorJson.detail !== null)
            ? (errorJson.detail.message || JSON.stringify(errorJson.detail))
            : (errorJson.detail || errorJson.message || errorText);
        } catch (e) { /* Ignore */ }
        throw new Error(`Failed to embed document: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error embedding uploaded document:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  }
};

export const LangChainService = {
  async askQuestion(query: string): Promise<QAResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/langchain/qa`, getFetchOptions('POST', { query }));

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to ask question: ${errorText}`);
    }

    return response.json();
  },



  async streamingQA(query: string): Promise<ReadableStream> {
    const response = await fetchWithAuth(`${API_BASE_URL}/langchain/qa/stream`, getFetchOptions('POST', { query }));

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to start streaming QA: ${errorText}`);
    }

    if (!response.body) {
      throw new Error('No response body for streaming');
    }

    return response.body;
  }
}; 